import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useRef, useState, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import type { Contact, ContactRole } from "@somasi/shared";
import { CountryField } from "@/components/country-field";
import { useConfirmDelete } from "@/components/confirm-dialog";
import { api, type ContactDraft } from "@/integrations/aws/resources";
import {
  AttachmentField,
  useClientGroupOptions,
  type Attachment,
} from "@/components/attachment-field";
import { ClientGroupSelect, CompanySelect } from "@/components/entity-select";
import { PageHeader } from "@/components/app-shell";
import { Button } from "@/components/ui/button";
import { RowAction, RowActions } from "@/components/row-actions";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Truncated } from "@/components/truncated";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Plus, Pencil, Trash2, X } from "lucide-react";
import { notify } from "@/lib/notify";
import { EmailInput } from "@/components/email-input";
import { useAuth } from "@/lib/auth";
import { optionalEmailSchema } from "@/lib/email";
import { ExportMenu } from "./client-groups";
import { z } from "zod";
import {
  useRowSelection,
  BulkBar,
  SelectAllCheckbox,
  RowCheckbox,
} from "@/components/bulk-actions";
import {
  ListPage,
  ListPageHeader,
  ListPageTable,
  ListSearch,
  ListToolbar,
  SortHeader,
  TableMessageRow,
  TablePagination,
  useDebounced,
  useListSort,
  usePagination,
} from "@/components/list-controls";

export const Route = createFileRoute("/_authenticated/contacts")({
  head: () => ({
    meta: [
      { title: "Contacts — Somasi Ops" },
      {
        name: "description",
        content: "Directory of every contact person across Somasi's client groups and companies.",
      },
    ],
  }),
  component: ContactsPage,
});

const ROLE_TYPES = [
  { value: "primary", label: "Primary contact" },
  { value: "authorised_person", label: "Authorised person" },
  { value: "director", label: "Director" },
  { value: "shareholder", label: "Shareholder" },
  { value: "beneficial_owner", label: "Beneficial owner" },
  { value: "billing", label: "Billing contact" },
  { value: "accounts", label: "Accounts contact" },
  { value: "afr", label: "AFR contact" },
  { value: "compliance", label: "Compliance contact" },
  { value: "operations", label: "Operations contact" },
  { value: "payroll", label: "Payroll contact" },
  { value: "audit", label: "Audit contact" },
  { value: "legal", label: "Legal contact" },
  { value: "corporate_secretarial", label: "Corporate secretarial" },
  { value: "document_recipient", label: "Document recipient" },
  { value: "invoice_recipient", label: "Invoice recipient" },
  { value: "payment_follow_up", label: "Payment follow-up" },
  { value: "other", label: "Other" },
] as const;
const ROLE_LABEL = Object.fromEntries(ROLE_TYPES.map((r) => [r.value, r.label]));

/** One company or client group a contact is attached to, with the roles held there. */
type Affiliation = {
  /** Stable across renders and unique per place, so it can key a list. */
  key: string;
  kind: "company" | "client_group";
  id: string;
  name: string;
  /** For a company, the client group it came with. */
  clientGroupName: string | null;
  roles: ContactRole[];
  /** The contact's primary affiliation — the one an invoice bills through. */
  isPrimary: boolean;
};

const placeKey = (role: ContactRole) =>
  role.company_id ? `company:${role.company_id}` : `group:${role.client_group_id}`;

/**
 * The contact's affiliation map: every company and client group they are attached
 * to, each with the roles they hold there.
 *
 * One person may be attached to several companies and several client groups at
 * once and hold several roles at each, so the set of role rows — not the contact's
 * own `company_id` — is the real answer to "who are they attached to". That one
 * place is still the *primary* affiliation, so it leads, and it appears even with
 * no role recorded against it: a contact attached to a company should not read as
 * attached to nothing.
 *
 * Companies come before bare client groups, because a company is the more specific
 * answer and a contact attached to a group alone has only the group to show.
 */
function affiliationsOf(contact: Contact, roles: ContactRole[]): Affiliation[] {
  const places = new Map<string, Affiliation>();

  if (contact.company_id) {
    places.set(`company:${contact.company_id}`, {
      key: `company:${contact.company_id}`,
      kind: "company",
      id: contact.company_id,
      name: contact.company_name ?? "Unnamed company",
      clientGroupName: contact.client_group_name,
      roles: [],
      isPrimary: true,
    });
  } else if (contact.client_group_id) {
    places.set(`group:${contact.client_group_id}`, {
      key: `group:${contact.client_group_id}`,
      kind: "client_group",
      id: contact.client_group_id,
      name: contact.client_group_name ?? "Unnamed client group",
      clientGroupName: null,
      roles: [],
      isPrimary: true,
    });
  }

  const unplaced: ContactRole[] = [];

  for (const role of roles) {
    // A role naming neither predates this model. The server reads one as covering
    // the contact's own group, so the primary attachment is where it belongs —
    // collected here and folded in below, once that place is known to exist.
    if (!role.company_id && !role.client_group_id) {
      unplaced.push(role);
      continue;
    }

    const key = placeKey(role);
    const existing = places.get(key);
    if (existing) {
      existing.roles.push(role);
      continue;
    }

    places.set(
      key,
      role.company_id
        ? {
            key,
            kind: "company",
            id: role.company_id,
            name: role.company_name ?? "Unnamed company",
            clientGroupName: role.client_group_name,
            roles: [role],
            isPrimary: false,
          }
        : {
            key,
            kind: "client_group",
            id: role.client_group_id!,
            name: role.client_group_name ?? "Unnamed client group",
            clientGroupName: null,
            roles: [role],
            isPrimary: false,
          },
    );
  }

  if (unplaced.length > 0) {
    const home = [...places.values()].find((place) => place.isPrimary);
    if (home) home.roles.push(...unplaced);
    // With no primary either, the roles have nowhere at all. They are still
    // shown, under an attachment with nothing chosen, so saving the contact
    // asks where they belong rather than dropping them.
    else
      places.set("unplaced", {
        key: "unplaced",
        kind: "company",
        id: "",
        name: "No company or client group recorded",
        clientGroupName: null,
        roles: unplaced,
        isPrimary: false,
      });
  }

  // Primary first, then companies, then client groups, each by name — a stable
  // order, so a row does not reshuffle as roles are added.
  const rank = (a: Affiliation) => (a.isPrimary ? 0 : a.kind === "company" ? 1 : 2);
  return [...places.values()].sort((a, b) => rank(a) - rank(b) || a.name.localeCompare(b.name));
}

/** One shared empty array, so "no roles" is a stable prop rather than a new one. */
const EMPTY_ROLES: ContactRole[] = [];

const roleLabels = (roles: ContactRole[]) => roles.map((r) => ROLE_LABEL[r.role] ?? r.role);

const schema = z.object({
  full_name: z.string().trim().min(1, "Name required").max(200),
  client_group_id: z.string().optional().or(z.literal("")),
  job_title: z.string().max(120).optional().or(z.literal("")),
  primary_email: optionalEmailSchema,
  secondary_email: optionalEmailSchema,
  primary_phone: z.string().max(40).optional().or(z.literal("")),
  secondary_phone: z.string().max(40).optional().or(z.literal("")),
  whatsapp_number: z.string().max(40).optional().or(z.literal("")),
  preferred_channel: z.string().max(30).optional().or(z.literal("")),
  country: z.string().max(80).optional().or(z.literal("")),
  time_zone: z.string().max(80).optional().or(z.literal("")),
  is_active: z.boolean(),
  notes: z.string().max(2000).optional().or(z.literal("")),
});
type FormT = z.infer<typeof schema>;

function ContactsPage() {
  const { canEdit } = useAuth();
  const qc = useQueryClient();
  const confirmDelete = useConfirmDelete();
  const [search, setSearch] = useState("");
  const [groupFilter, setGroupFilter] = useState<string>("all");
  // `""` is "every company", which is what the picker clears to. Both filters
  // search the API rather than holding the register, so there is nothing here
  // to fetch up front.
  const [companyFilter, setCompanyFilter] = useState<string>("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<Contact | null>(null);

  // One request brings the contacts and the roles each one holds; the server
  // groups them, so the page no longer reads the whole role table separately.
  // The company and client group filters are applied by the API, so changing
  // either refetches; both on "all" sends nothing but the sort, making the
  // default view `GET /contacts?include=roles&order=updated_at.desc` — whatever
  // changed most recently, first. The roles still ride along in the same request.
  //
  // Both filters ask "who works with this?", not "whose primary affiliation is
  // this?" — a person holding roles at three companies is primary at one of them
  // at most, so matching on the contact's own column would hide them from the
  // other two companies' own filters. The server reads the role rows under the
  // company or group and ORs them with the primary affiliation.
  // Held back so a search is one request when typing stops, not one per key.
  const query = useDebounced(search.trim());
  //
  // `updated_at` is not a column on screen, but it stays the initial sort so the
  // page still opens on what was just touched; a header click moves off it.
  const sort = useListSort({ column: "updated_at", ascending: false });
  const filters = { group: groupFilter, company: companyFilter, query, sort: sort.sort };
  const pager = usePagination({ resetKey: filters });

  /**
   * The list the filters describe — the table pages it, the export takes it whole.
   *
   * The text box used to filter the rows already on screen, which was fine while
   * the page held every contact and is wrong now that it holds twenty of them:
   * a search has to reach the contacts that are not on this page. So it goes to
   * the API as one OR group over the three columns it always matched.
   */
  const filteredList = () => {
    const list = api.contacts
      .listWithRoles({
        servesCompanyId: companyFilter || undefined,
        servesClientGroupId: groupFilter === "all" ? undefined : groupFilter,
      })
      .order(sort.sort.column, { ascending: sort.sort.ascending });
    // Ties — every active contact, say — fall back to the name, so a page break
    // never lands somewhere different between two requests.
    if (sort.sort.column !== "full_name") list.order("full_name");

    if (query) {
      list.or([
        { column: "full_name", op: "ilike", value: `%${query}%` },
        { column: "primary_email", op: "ilike", value: `%${query}%` },
      ]);
    }
    return list;
  };

  const {
    data: result,
    isLoading,
    isFetching,
  } = useQuery({
    queryKey: ["contacts", { ...filters, page: pager.page, pageSize: pager.pageSize }],
    queryFn: async () => {
      const { data, error, count } = await filteredList().page(pager.page, pager.pageSize);
      if (error) throw error;
      return { rows: data ?? [], total: count ?? 0 };
    },
    // Keep the rows on screen while the new filter loads.
    placeholderData: (previous) => previous,
  });

  const data = result?.rows ?? [];
  const total = result?.total;

  const rolesByContact = useMemo(() => {
    const m = new Map<string, ContactRole[]>();
    for (const c of data) m.set(c.id, c.roles ?? []);
    return m;
  }, [data]);

  // Every company and client group each contact is attached to, with their roles
  // at each. Derived once per fetch: the table, the export and the dialog all read
  // the same shape.
  const affiliationsByContact = useMemo(() => {
    const m = new Map<string, Affiliation[]>();
    for (const c of data) m.set(c.id, affiliationsOf(c, c.roles ?? []));
    return m;
  }, [data]);

  // Every filter is the API's, so the rows on screen are already the answer.
  const filtered = data;

  const sel = useRowSelection(filtered);

  // Four data columns, plus the checkbox and actions columns editors get.
  const columnCount = canEdit ? 6 : 4;
  const filtersSet = Boolean(query) || groupFilter !== "all" || Boolean(companyFilter);

  // Referentially stable, so the dialog is not handed a fresh array on every
  // render of this page — the dialog loads its fields once per opening, and a
  // changing prop identity is exactly the sort of thing that used to re-trigger
  // that and wipe a half-filled form.
  const editingRoles = useMemo(
    () => (editing ? (rolesByContact.get(editing.id) ?? EMPTY_ROLES) : EMPTY_ROLES),
    [editing, rolesByContact],
  );

  const openNew = () => {
    setEditing(null);
    setDialogOpen(true);
  };
  const openEdit = (c: Contact) => {
    setEditing(c);
    setDialogOpen(true);
  };
  const remove = async (c: Contact) => {
    if (!(await confirmDelete(`contact “${c.full_name}”`))) return;
    const { error } = await api.contacts.remove(c.id);
    if (error) return notify.error(error);
    notify.success("Contact deleted");
    qc.invalidateQueries({ queryKey: ["contacts"] });
  };

  /**
   * One row per attachment, not per contact.
   *
   * A person attached to three companies used to be one row with the companies
   * packed into a cell, which reads but does not filter or pivot: "every contact
   * at this company" could not be answered by sorting the sheet. So each company
   * or client group they are attached to gets its own row, carrying that place in
   * the Company / Client Group columns and the roles held *there* — joined into a
   * single Roles cell, since the roles at one place are a short list rather than a
   * dimension of their own.
   *
   * The person's own fields repeat down their rows; Attachment says which of them
   * is the primary one, so the duplication is readable rather than ambiguous. A
   * contact attached to nothing still gets a row, with those columns blank.
   */
  const exportRows = async () => {
    const { data: all, error } = await filteredList().all();
    if (error) throw new Error(error.message);
    return (all ?? []).flatMap((c) => {
      // Spread around the attachment columns rather than before them, so the
      // sheet reads name → where they are attached → how to reach them.
      const lead = {
        Name: c.full_name,
        "Job Title": c.job_title ?? "",
      };
      const contactDetails = {
        "Primary Email": c.primary_email ?? "",
        "Primary Phone": c.primary_phone ?? "",
        WhatsApp: c.whatsapp_number ?? "",
        Country: c.country ?? "",
        Active: c.is_active ? "Yes" : "No",
      };

      // Worked out here rather than read from `affiliationsByContact`, which
      // only covers the page on screen.
      const places = affiliationsOf(c, c.roles ?? []);
      if (places.length === 0) {
        return [
          {
            ...lead,
            Company: "",
            "Client Group": "",
            Roles: "",
            Attachment: "",
            ...contactDetails,
          },
        ];
      }

      return places.map((place) => ({
        ...lead,
        // The placeholder attachment — roles that name no company or group — has
        // no place to name, so it exports its roles against blank columns rather
        // than against its on-screen label.
        Company: place.kind === "company" && place.id ? place.name : "",
        "Client Group": place.kind === "client_group" ? place.name : (place.clientGroupName ?? ""),
        Roles: roleLabels(place.roles).join(", "),
        Attachment: place.isPrimary ? "Primary" : place.id ? "Additional" : "Unassigned",
        ...contactDetails,
      }));
    });
  };

  return (
    <ListPage>
      <ListPageHeader>
        <PageHeader
          title="Contacts"
          description="Every person you work with — with the right role, so reminders and invoices reach the right inbox."
          actions={
            <>
              <ExportMenu
                filenameBase="somasi-contacts"
                rows={exportRows}
                pdfCols={[
                  { header: "Name", dataKey: "Name" },
                  { header: "Company", dataKey: "Company" },
                  { header: "Client Group", dataKey: "Client Group" },
                  { header: "Roles", dataKey: "Roles" },
                  { header: "Attachment", dataKey: "Attachment" },
                  { header: "Email", dataKey: "Primary Email" },
                  { header: "Phone", dataKey: "Primary Phone" },
                ]}
                title="Contacts Directory"
              />
              {canEdit && (
                <Button onClick={openNew}>
                  <Plus className="mr-2 h-4 w-4" /> New contact
                </Button>
              )}
            </>
          }
        />

        <ListToolbar>
          <ListSearch value={search} onChange={setSearch} placeholder="Search by name or email…" />
          <CompanySelect
            value={companyFilter}
            onChange={setCompanyFilter}
            placeholder="All companies"
            clearable
            clearLabel="All companies"
            showClientGroup={false}
            className="w-56"
            contentClassName="w-72 p-0"
          />
          <ClientGroupSelect
            value={groupFilter === "all" ? "" : groupFilter}
            onChange={(id) => setGroupFilter(id || "all")}
            placeholder="All client groups"
            clearable
            clearLabel="All client groups"
            className="w-56"
            contentClassName="w-72 p-0"
          />
          {isFetching && <div className="text-xs text-muted-foreground">Loading…</div>}
        </ListToolbar>

        {canEdit && (
          <BulkBar
            sel={sel}
            resource={api.contacts}
            entityLabel="contact"
            activeColumn="is_active"
            onDone={() => qc.invalidateQueries({ queryKey: ["contacts"] })}
          />
        )}
      </ListPageHeader>

      <ListPageTable>
        <Table containerClassName="min-h-0 flex-1">
          <TableHeader sticky>
            <TableRow>
              {canEdit && (
                <TableHead className="w-10">
                  <SelectAllCheckbox sel={sel} />
                </TableHead>
              )}
              <SortHeader sort={sort} column="full_name">
                Name
              </SortHeader>
              {/* Built from the role rows and the primary affiliation, so there is
                  no single column to order by. */}
              <TableHead>Attached to</TableHead>
              {/* The email leads the cell, so that is what the column sorts by. */}
              <SortHeader sort={sort} column="primary_email">
                Contact
              </SortHeader>
              <SortHeader sort={sort} column="is_active">
                Status
              </SortHeader>
              {/* Edit and delete are the only actions, so a read-only operator
                  would get an empty column. */}
              {canEdit && (
                <TableHead className="w-px whitespace-nowrap text-right">Actions</TableHead>
              )}
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableMessageRow colSpan={columnCount}>Loading…</TableMessageRow>
            ) : filtered.length === 0 ? (
              <TableMessageRow colSpan={columnCount}>
                {filtersSet ? "No contacts match these filters." : "No contacts yet."}
              </TableMessageRow>
            ) : (
              filtered.map((c) => {
                const places = affiliationsByContact.get(c.id) ?? [];
                return (
                  <TableRow key={c.id} data-state={sel.isSelected(c.id) ? "selected" : undefined}>
                    {canEdit && (
                      <TableCell>
                        <RowCheckbox sel={sel} id={c.id} />
                      </TableCell>
                    )}
                    <TableCell className="max-w-xs">
                      <Truncated text={c.full_name} className="font-medium" />
                      {c.job_title && (
                        <Truncated text={c.job_title} className="text-xs text-muted-foreground" />
                      )}
                    </TableCell>
                    {/* Every company and client group, each with the roles held
                        there. Capped at two so a person attached to a dozen
                        companies does not stretch the row; the rest are a count,
                        and the dialog has the full set. */}
                    <TableCell className="min-w-56">
                      {places.length === 0 ? (
                        <span className="text-xs text-muted-foreground">—</span>
                      ) : (
                        <div className="space-y-1.5">
                          {places.slice(0, 2).map((place) => (
                            <div key={place.key}>
                              <div className="flex items-center gap-1 text-xs">
                                <span className={place.isPrimary ? "font-medium" : undefined}>
                                  {place.name}
                                </span>
                                {place.kind === "client_group" && (
                                  <Badge variant="outline" className="text-[10px]">
                                    Group
                                  </Badge>
                                )}
                              </div>
                              {/* The group a company brought with it. */}
                              {place.kind === "company" && place.clientGroupName && (
                                <div className="text-[11px] text-muted-foreground">
                                  {place.clientGroupName}
                                </div>
                              )}
                              <div className="mt-0.5 flex flex-wrap gap-1">
                                {place.roles.length === 0 ? (
                                  <span className="text-[11px] text-muted-foreground">
                                    No roles yet
                                  </span>
                                ) : (
                                  place.roles.map((r) => (
                                    <Badge key={r.id} variant="secondary" className="text-[10px]">
                                      {ROLE_LABEL[r.role] ?? r.role}
                                    </Badge>
                                  ))
                                )}
                              </div>
                            </div>
                          ))}
                          {places.length > 2 && (
                            <div className="text-[11px] text-muted-foreground">
                              +{places.length - 2} more
                            </div>
                          )}
                        </div>
                      )}
                    </TableCell>
                    <TableCell>
                      <Truncated text={c.primary_email} className="text-xs" />
                      <div className="text-xs text-muted-foreground">{c.primary_phone ?? ""}</div>
                    </TableCell>
                    <TableCell>
                      {c.is_active ? (
                        <Badge className="bg-success text-success-foreground">Active</Badge>
                      ) : (
                        <Badge variant="outline">Inactive</Badge>
                      )}
                    </TableCell>
                    {canEdit && (
                      <TableCell>
                        <RowActions>
                          <RowAction label="Edit" onClick={() => openEdit(c)}>
                            <Pencil />
                          </RowAction>
                          <RowAction label="Delete" destructive onClick={() => remove(c)}>
                            <Trash2 />
                          </RowAction>
                        </RowActions>
                      </TableCell>
                    )}
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
        <TablePagination pagination={pager} total={total} loading={isLoading} unit="contact" />
      </ListPageTable>

      <ContactDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        editing={editing}
        existingRoles={editingRoles}
        onSaved={() => {
          // The roles ride along with the contacts query now.
          qc.invalidateQueries({ queryKey: ["contacts"] });
        }}
      />
    </ListPage>
  );
}

/** One role, as the dialog edits it inside its attachment. */
type RoleDraft = { id?: string; role: ContactRole["role"] };

/** One company or client group being attached, with the roles held there. */
type AffiliationDraft = {
  /** Stable while the dialog is open, so a React list key survives a re-pick. */
  key: string;
  attachment: Attachment;
  roles: RoleDraft[];
};

/** A new, unpicked attachment carrying one role. */
const blankBlock = (): AffiliationDraft => ({
  // Local and unique: the saved key is the place, which is not chosen yet.
  key: `new:${crypto.randomUUID()}`,
  attachment: null,
  roles: [{ role: "primary" }],
});

function ContactDialog({
  open,
  onOpenChange,
  editing,
  existingRoles,
  onSaved,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  editing: Contact | null;
  existingRoles: ContactRole[];
  onSaved: () => void;
}) {
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState<FormT>({
    full_name: "",
    client_group_id: "",
    job_title: "",
    primary_email: "",
    secondary_email: "",
    primary_phone: "",
    secondary_phone: "",
    whatsapp_number: "",
    preferred_channel: "email",
    country: "",
    time_zone: "",
    is_active: true,
    notes: "",
  });
  // The attachments are their own state rather than form fields: each is one
  // choice that writes an id, and the picker needs the name to display.
  const [blocks, setBlocks] = useState<AffiliationDraft[]>([]);
  // Which attachment is the primary one — the contact's own `company_id` or
  // `client_group_id`, and so the one an invoice bills through. Held by key
  // rather than by index, so reordering or removing another entry cannot move it.
  const [primaryKey, setPrimaryKey] = useState<string | null>(null);

  const primary = blocks.find((b) => b.key === primaryKey) ?? blocks[0] ?? null;

  /**
   * The entry the dialog adds by itself when the contact is attached to nothing,
   * so the picker is there without a click on Add. Left unpicked it is ignored on
   * save rather than refused — a contact attached to nothing is still allowed.
   */
  const starterKey = useRef<string | null>(null);
  const isUnusedStarter = (block: AffiliationDraft) =>
    !block.attachment && block.key === starterKey.current;

  /**
   * Which contact the fields below were loaded for, so they are loaded once per
   * opening rather than whenever a prop changes identity.
   *
   * `existingRoles` cannot be reacted to. For a new contact the page passes a
   * fresh `[]` on every one of its own renders, and for a saved one it is rebuilt
   * whenever the contacts query refetches — so reacting to it resets the form and
   * the attachment list out from under whoever is filling them in. That is what
   * closed the picker mid-choice: opening the attachment picker filled a company
   * cache the page's own filter subscribed to, the page re-rendered, and the
   * reset ran and unmounted the entry being edited.
   */
  const loadedFor = useRef<string | null>(null);

  useEffect(() => {
    if (!open) {
      // Reopening reloads, even for the same contact.
      loadedFor.current = null;
      return;
    }

    const subject = editing?.id ?? "new";
    if (loadedFor.current === subject) return;
    loadedFor.current = subject;

    // Reopening a saved contact rebuilds the attachment list from the same shape
    // the table renders, so the dialog and the row always agree on what the
    // person is attached to and in what order.
    const places = editing ? affiliationsOf(editing, existingRoles) : [];
    const starter = places.length === 0 ? blankBlock() : null;
    starterKey.current = starter?.key ?? null;
    setBlocks(
      starter
        ? [starter]
        : places.map((place) => ({
            key: place.key,
            // A place with no id is the bucket of roles that named nothing; it opens
            // with the picker empty, so saving asks where those roles belong.
            attachment: !place.id
              ? null
              : place.kind === "company"
                ? {
                    kind: "company",
                    id: place.id,
                    name: place.name,
                    clientGroupName: place.clientGroupName,
                  }
                : { kind: "client_group", id: place.id, name: place.name },
            roles: place.roles.map((r) => ({ id: r.id, role: r.role })),
          })),
    );
    setPrimaryKey(
      starter?.key ?? places.find((place) => place.isPrimary)?.key ?? places[0]?.key ?? null,
    );

    setForm({
      full_name: editing?.full_name ?? "",
      client_group_id: editing?.client_group_id ?? "",
      job_title: editing?.job_title ?? "",
      primary_email: editing?.primary_email ?? "",
      secondary_email: editing?.secondary_email ?? "",
      primary_phone: editing?.primary_phone ?? "",
      secondary_phone: editing?.secondary_phone ?? "",
      whatsapp_number: editing?.whatsapp_number ?? "",
      preferred_channel: editing?.preferred_channel ?? "email",
      country: editing?.country ?? "",
      time_zone: editing?.time_zone ?? "",
      is_active: editing?.is_active ?? true,
      notes: editing?.notes ?? "",
    });
  }, [open, editing, existingRoles]);

  /**
   * What the attachment list cannot express, said before anything is written.
   *
   * The server refuses all three too, but it sees one flat list of roles and can
   * only name the row — here each one can name the company or group it is about.
   */
  const describeProblem = (): string | null => {
    const seen = new Set<string>();

    for (const block of blocks) {
      if (isUnusedStarter(block)) continue;
      const place = block.attachment;
      if (!place) return "Choose a company or client group for every attachment, or remove it.";

      const id = `${place.kind}:${place.id}`;
      if (seen.has(id)) return `${place.name} is attached twice.`;
      seen.add(id);

      // An attachment is stored as the roles held there, so one with no roles has
      // nothing to store — except the primary, which the contact's own columns
      // hold. Saying so beats letting it vanish on reopen.
      if (block.roles.length === 0 && block.key !== (primary?.key ?? null)) {
        return `Add a role at ${place.name}, or remove it — an attachment is held by its roles.`;
      }

      const roles = new Set<string>();
      for (const role of block.roles) {
        if (roles.has(role.role)) {
          return `${ROLE_LABEL[role.role] ?? role.role} is listed twice at ${place.name}.`;
        }
        roles.add(role.role);
      }
    }

    return null;
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    const parsed = schema.safeParse(form);
    if (!parsed.success) return notify.error(parsed.error.issues[0].message);

    const problem = describeProblem();
    if (problem) return notify.error(problem);

    setSaving(true);
    const payload: Record<string, unknown> = { ...parsed.data };
    for (const k of Object.keys(payload)) if (payload[k] === "") payload[k] = null;

    // The contact's own two columns hold the *primary* attachment only — one id,
    // and the server derives the rest, so attaching to a company also records that
    // company's client group. Every other attachment lives in the roles below.
    payload.company_id = primary?.attachment?.kind === "company" ? primary.attachment.id : null;
    payload.client_group_id =
      primary?.attachment?.kind === "client_group" ? primary.attachment.id : null;

    const { data: saved, error: saveError } = editing
      ? await api.contacts.update(editing.id, payload as ContactDraft)
      : await api.contacts.create(payload as ContactDraft);

    if (saveError || !saved) {
      setSaving(false);
      return notify.error(saveError?.message ?? "Failed");
    }
    const contactId = saved.id;

    // The dialog holds the contact's whole role set, so it is sent whole and
    // swapped in one transaction — the old delete-then-insert from here could
    // leave a contact with no roles if the second call never landed.
    //
    // Each role names its own place, and only one of the two ids: a company
    // carries its own client group, so the server reads the group off it rather
    // than being told. That is what lets one person hold roles at companies in
    // different groups.
    if (contactId) {
      const { error } = await api.contacts.replaceRoles(
        contactId,
        blocks.flatMap((block) => {
          const place = block.attachment;
          if (!place) return [];
          return block.roles.map((r) => ({
            id: r.id,
            role: r.role,
            company_id: place.kind === "company" ? place.id : null,
            client_group_id: place.kind === "client_group" ? place.id : null,
          }));
        }),
      );
      if (error) {
        setSaving(false);
        return notify.error(`Roles: ${error.message}`);
      }
    }

    setSaving(false);
    notify.success(editing ? "Contact updated" : "Contact created");
    onOpenChange(false);
    onSaved();
  };

  const patchBlock = (key: string, patch: Partial<Omit<AffiliationDraft, "key">>) =>
    setBlocks((current) => current.map((b) => (b.key === key ? { ...b, ...patch } : b)));

  /**
   * Adds an attachment, already carrying one role.
   *
   * An attachment is stored as the roles held there, so an empty one would have
   * nowhere to live — starting with a role means that is the rare case of someone
   * deleting the last one rather than the normal path.
   */
  const addBlock = () => {
    const block = blankBlock();
    setBlocks((current) => [...current, block]);
    setPrimaryKey((current) => current ?? block.key);
  };

  const removeBlock = (key: string) => {
    setBlocks((current) => current.filter((b) => b.key !== key));
    // The next one along takes over as primary, so the contact never ends up
    // attached to things with none of them primary.
    setPrimaryKey((current) =>
      current === key ? (blocks.find((b) => b.key !== key)?.key ?? null) : current,
    );
  };

  /**
   * Adds a role at one attachment, defaulting to one not held there yet.
   *
   * The same role twice at the same place is refused, so defaulting to a fixed
   * value would hand back an error on the second click rather than a usable row.
   */
  const addRole = (key: string, roles: RoleDraft[]) => {
    const taken = new Set(roles.map((r) => r.role));
    const next = ROLE_TYPES.find((rt) => !taken.has(rt.value))?.value ?? "other";
    patchBlock(key, { roles: [...roles, { role: next }] });
  };
  const patchRole = (key: string, roles: RoleDraft[], idx: number, patch: Partial<RoleDraft>) =>
    patchBlock(key, { roles: roles.map((r, i) => (i === idx ? { ...r, ...patch } : r)) });
  const removeRole = (key: string, roles: RoleDraft[], idx: number) =>
    patchBlock(key, { roles: roles.filter((_, i) => i !== idx) });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{editing ? "Edit contact" : "New contact"}</DialogTitle>
          <DialogDescription>
            Add contact details and the roles this person plays.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="flex flex-col gap-4">
          <div className="space-y-4">
            <div className="space-y-2">
              <Label required>Full name</Label>
              <Input
                value={form.full_name}
                onChange={(e) => setForm({ ...form, full_name: e.target.value })}
                required
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Job title</Label>
                <Input
                  value={form.job_title}
                  onChange={(e) => setForm({ ...form, job_title: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Status</Label>
                <Select
                  value={form.is_active ? "active" : "inactive"}
                  onValueChange={(v) => setForm({ ...form, is_active: v === "active" })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="inactive">Inactive</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Attachments, each with its own roles */}
            <div className="space-y-3 rounded-md border p-3">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <Label>Attached to</Label>
                  <p className="text-xs text-muted-foreground">
                    A company, a client group, or any number of both — with the roles this person
                    holds at each. Attaching a company brings its client group with it.
                  </p>
                </div>
                <Button type="button" variant="outline" size="sm" onClick={addBlock}>
                  <Plus className="mr-1 h-3 w-3" /> Add
                </Button>
              </div>

              {blocks.length === 0 && (
                <p className="text-xs text-muted-foreground">Not attached to anything yet.</p>
              )}

              {blocks.map((block) => {
                const isPrimary = block.key === (primary?.key ?? null);
                // What the other entries hold, so the same company or client
                // group is not offered twice. This entry's own choice is left in,
                // so it stays visible and clearable.
                const taken = blocks
                  .filter((other) => other.key !== block.key)
                  .map((other) => other.attachment?.id)
                  .filter((id): id is string => !!id);
                return (
                  <div key={block.key} className="space-y-2 rounded-md border bg-muted/30 p-3">
                    <div className="flex items-start gap-2">
                      <AttachmentField
                        label={null}
                        id={`attachment-${block.key}`}
                        className="min-w-0 flex-1 space-y-2"
                        value={block.attachment}
                        excludeIds={taken}
                        onChange={(next) => patchBlock(block.key, { attachment: next })}
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        aria-label="Remove attachment"
                        onClick={() => removeBlock(block.key)}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>

                    {/* Exactly one attachment is the primary: the one written to
                        the contact's own columns, which is what an invoice bills
                        through. Picking another hands it over rather than adding
                        a second. */}
                    <label className="flex items-center gap-2 text-xs font-medium text-foreground">
                      <Switch
                        checked={isPrimary}
                        // The primary cannot be switched off — it is handed over
                        // by switching another on. Disabled says that, but the
                        // default dimming would make the one attachment that *is*
                        // primary look like the dead control on the dialog, so it
                        // stays at full strength and only loses the pointer.
                        disabled={isPrimary}
                        className="disabled:cursor-default disabled:opacity-100"
                        onCheckedChange={() => setPrimaryKey(block.key)}
                      />
                      {isPrimary ? "Primary attachment" : "Make primary"}
                    </label>

                    <div className="space-y-2 border-t pt-2">
                      {block.roles.length === 0 && (
                        <p className="text-xs text-muted-foreground">
                          No roles here yet — an attachment is held by its roles.
                        </p>
                      )}

                      {block.roles.map((r, i) => (
                        <div
                          key={r.id ?? i}
                          className="grid grid-cols-[1fr_auto] items-center gap-2"
                        >
                          <Select
                            value={r.role}
                            onValueChange={(v) =>
                              patchRole(block.key, block.roles, i, {
                                role: v as ContactRole["role"],
                              })
                            }
                          >
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {ROLE_TYPES.map((rt) => (
                                <SelectItem key={rt.value} value={rt.value}>
                                  {rt.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            aria-label="Remove role"
                            onClick={() => removeRole(block.key, block.roles, i)}
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                      ))}

                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="h-7 px-2 text-xs"
                        onClick={() => addRole(block.key, block.roles)}
                      >
                        <Plus className="mr-1 h-3 w-3" /> Add role
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="contact-primary-email">Primary email</Label>
                <EmailInput
                  id="contact-primary-email"
                  value={form.primary_email}
                  onChange={(primary_email) => setForm({ ...form, primary_email })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="contact-secondary-email">Secondary email</Label>
                <EmailInput
                  id="contact-secondary-email"
                  value={form.secondary_email}
                  onChange={(secondary_email) => setForm({ ...form, secondary_email })}
                />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-2">
                <Label>Primary phone</Label>
                <Input
                  value={form.primary_phone}
                  onChange={(e) => setForm({ ...form, primary_phone: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Secondary phone</Label>
                <Input
                  value={form.secondary_phone}
                  onChange={(e) => setForm({ ...form, secondary_phone: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>WhatsApp</Label>
                <Input
                  value={form.whatsapp_number}
                  onChange={(e) => setForm({ ...form, whatsapp_number: e.target.value })}
                />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-2">
                <Label>Preferred channel</Label>
                <Select
                  value={form.preferred_channel || "email"}
                  onValueChange={(v) => setForm({ ...form, preferred_channel: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="email">Email</SelectItem>
                    <SelectItem value="phone">Phone</SelectItem>
                    <SelectItem value="whatsapp">WhatsApp</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <CountryField
                className=""
                value={form.country ?? ""}
                onChange={(country) => setForm({ ...form, country })}
              />
              <div className="space-y-2">
                <Label>Time zone</Label>
                <Input
                  value={form.time_zone}
                  onChange={(e) => setForm({ ...form, time_zone: e.target.value })}
                  placeholder="e.g. GMT-4"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea
                rows={2}
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={saving}>
              {saving ? "Saving…" : editing ? "Save changes" : "Create contact"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
